import { FC, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';

import { useI18nValues } from '@/entities/vfo/hooks/useI18nValues';
import { AccessLevel, Tags } from '@/entities/vfo/types';
import { useNotices } from '@/features/notices/useNotices';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { usePermissions } from '@/features/vfo/hooks/usePermissions';
import { getPersonFullName } from '@/shared/helpers/names';
import { Icon } from '@/shared/uikit/Icon';
import { Title } from '@/shared/uikit/Title';
import { Button } from '@/shared/uikitV2/Button';
import { Radio } from '@/shared/uikitV2/Radio';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { BtnClose } from '@/widgets/modals/components/BtnClose';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { AccessLevelModalProps } from '@/widgets/modals/types';

import {
  Body,
  CollapsibleInner,
  CollapsibleWrapper,
  Container,
  Footer,
  Header,
  IconStyled,
  Item,
  ItemContent,
  ItemTitle,
  ItemTitleWrapper,
  List,
  MembersSelect,
  RadioItem,
  RadioWrapper,
  Separator,
  StyledDrawer,
  SubTitle,
} from './styled';

const levels = [AccessLevel.BASIC, AccessLevel.CONNECTED, AccessLevel.DELEGATED];

type MemberTypes = 'all' | 'selected' | 'category';

type BackendData = {
  memberType: MemberTypes;
  allowedProfileIds: number[];
  allowedTagCodes: Tags[];
};

export const AccessLevelModal: FC<AccessLevelModalProps> = ({ member, openProfile }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'accessLevel' });
  const initialLevel = member.accessLevel ?? AccessLevel.BASIC;
  const [selectedLevel, setSelectedLevel] = useState<AccessLevel>(initialLevel);

  const [selectedMemberType, setSelectedMemberType] = useState<MemberTypes>('all');
  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tags[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const initialDataRef = useRef<BackendData | null>(null);

  const modals = useModalsStore();
  const notices = useNotices();
  const { tag, accessLevelDescription, accessLevel } = useI18nValues();
  const { getByProfileId } = useMembers();
  const { grantRole, updateChatPermissions, fetchMemberChatPermissions } = usePermissions();

  const selectedMembersNames = useMemo(() => {
    return selectedMembers
      .map(getByProfileId)
      .filter((m) => m !== undefined)
      .map((m) => getPersonFullName(m, 'display'))
      .join(', ');
  }, [selectedMembers]);

  const selectedTagNames = useMemo(() => {
    return [selectedTags]
      .flat()
      .map((item) => tag(item))
      .join(', ');
  }, [selectedTags]);

  const fetchPermissions = async () => {
    if (!member.profileId) return;
    const data = await fetchMemberChatPermissions(member.profileId);
    const allowedProfileIds = data?.allowedProfileIds ?? [];
    const allowedTagCodes = data?.allowedTagCodes ?? [];

    let memberType: MemberTypes = 'all';
    if (allowedTagCodes.length) {
      memberType = 'category';
    } else if (allowedProfileIds.length) {
      memberType = 'selected';
    }

    initialDataRef.current = {
      memberType,
      allowedProfileIds,
      allowedTagCodes,
    };

    setSelectedMembers(allowedProfileIds);
    setSelectedTags(allowedTagCodes);
    setSelectedMemberType(memberType);
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const onSave = async () => {
    if (!member.profileId) return;
    setIsLoading(true);
    const res = await grantRole(member.profileId, selectedLevel);

    if (res) {
      await updateChatPermissions(member.profileId, {
        canInitiateWithAll: selectedMemberType === 'all' && selectedLevel !== AccessLevel.BASIC,
        allowedProfileIds: selectedMemberType === 'selected' ? selectedMembers : [],
        allowedTagCodes: selectedMemberType === 'category' ? selectedTags : [],
      });

      notices.success(
        t('success', {
          fullname: getPersonFullName(member, 'display'),
          accessLevel: accessLevel(selectedLevel),
        }),
      );

      modals.closeModal();

      if (openProfile) {
        modals.openModal({
          type: 'VfoProfileModal',
          initialMember: {
            ...member,
            accessLevel: selectedLevel,
          },
        });
      }
    }

    setIsLoading(false);
  };

  const selectMemberType = (type: MemberTypes) => {
    setSelectedMembers([]);
    setSelectedTags([]);
    setSelectedMemberType(type);
  };

  const handleLevelChange = (level: AccessLevel) => {
    setSelectedLevel(level);

    if (level === initialLevel && initialDataRef.current) {
      setSelectedMembers(initialDataRef.current.allowedProfileIds);
      setSelectedTags(initialDataRef.current.allowedTagCodes);
      setSelectedMemberType(initialDataRef.current.memberType);
      return;
    }

    setSelectedMembers([]);
    setSelectedTags([]);
    setSelectedMemberType('all');
  };

  const showEditMembersButton = selectedMemberType === 'selected' && selectedMembers.length > 0;

  const showEditTagsButton = selectedMemberType === 'category' && selectedTags.length > 0;

  return (
    <StyledDrawer
      width={500}
      open={true}
      closable={false}
      footer={null}
      onClose={modals.closeModal}
    >
      <Container>
        <Header>
          <Title size={14} bold>
            {t('title')}
          </Title>
          <BtnClose onClick={modals.closeModal} />
        </Header>
        <Body className="uikitScrollbar">
          {levels.map((level) => {
            const data = accessLevelDescription(level);
            const isActive = selectedLevel === level;
            const showMembersSelect = level !== AccessLevel.BASIC;

            return (
              <Item
                key={level}
                className={cn({ active: isActive })}
                onClick={() => handleLevelChange(level)}
              >
                <Radio value={level} checked={isActive} />
                <ItemContent>
                  <ItemTitleWrapper>
                    <ItemTitle>{data.title}</ItemTitle>
                    <Button
                      type="link"
                      size="small"
                      iconPosition="end"
                      icon={
                        <IconStyled
                          name={'caret-down'}
                          size="s20"
                          className={cn({ active: isActive })}
                        />
                      }
                      style={{
                        fontWeight: 400,
                        height: 'auto',
                      }}
                    >
                      {t('moreInfo')}
                    </Button>
                  </ItemTitleWrapper>
                  <span>{data.description}</span>
                  <CollapsibleWrapper className={cn({ active: isActive })}>
                    <CollapsibleInner className={cn({ active: isActive })}>
                      <Separator />
                      <SubTitle>{data.allowsTitle}</SubTitle>
                      <List>
                        <li>{data.allows1}</li>
                        <li>{data.allows2}</li>
                        <li>{data.allows3}</li>
                      </List>
                      <SubTitle>{data.notAllowsTitle}</SubTitle>
                      <List>
                        <li>{data.notAllows1}</li>
                        <li>{data.notAllows2}</li>
                        {data.notAllows3 && <li>{data.notAllows3}</li>}
                      </List>

                      {showMembersSelect && (
                        <MembersSelect>
                          <b>{t('interactWith')}</b>
                          <span>{t('startNewCase')}</span>
                          <RadioWrapper>
                            <RadioItem>
                              <Radio
                                value="all"
                                checked={selectedMemberType === 'all'}
                                onChange={() => selectMemberType('all')}
                              />
                              <span>{t('allMembers')}</span>
                            </RadioItem>
                            <RadioItem>
                              <Radio
                                value="selected"
                                checked={selectedMemberType === 'selected'}
                                onChange={() => {
                                  if (selectedMemberType === 'selected') return;
                                  modals.openModal({
                                    type: 'SelectMembersModal',
                                    onFinish: (members) => {
                                      selectMemberType('selected');
                                      setSelectedMembers(members);
                                    },
                                    hideProfileIds: member.profileId ? [member.profileId] : [],
                                  });
                                }}
                              />
                              <div>
                                <div>{t('selectedMembers')}</div>
                                <div>{selectedMembersNames}</div>
                              </div>
                              {showEditMembersButton && (
                                <Button
                                  style={{
                                    marginLeft: 'auto',
                                    height: 'auto',
                                  }}
                                  size="small"
                                  type="text"
                                  icon={<Icon name="pencil" size="s20" />}
                                  onClick={() => {
                                    modals.openModal({
                                      type: 'SelectMembersModal',
                                      initialMembersProfileIds: selectedMembers,
                                      onFinish: setSelectedMembers,
                                      hideProfileIds: member?.profileId ? [member?.profileId] : [],
                                    });
                                  }}
                                >
                                  {t('edit')}
                                </Button>
                              )}
                            </RadioItem>
                            <RadioItem>
                              <Radio
                                value="category"
                                checked={selectedMemberType === 'category'}
                                onChange={() => {
                                  if (selectedMemberType === 'category') return;
                                  modals.openModal({
                                    type: 'SelectTagModal',
                                    onFinish: (tags) => {
                                      selectMemberType('category');
                                      setSelectedTags(tags);
                                    },
                                  });
                                }}
                              />
                              <div>
                                <div>{t('categoryMembers')}</div>
                                <div>{selectedTagNames}</div>
                              </div>
                              {showEditTagsButton && (
                                <Button
                                  style={{
                                    marginLeft: 'auto',
                                    height: 'auto',
                                  }}
                                  size="small"
                                  type="text"
                                  icon={<Icon name="pencil" size="s20" />}
                                  onClick={() => {
                                    modals.openModal({
                                      type: 'SelectTagModal',
                                      initialTags: selectedTags,
                                      onFinish: setSelectedTags,
                                    });
                                  }}
                                >
                                  {t('edit')}
                                </Button>
                              )}
                            </RadioItem>
                          </RadioWrapper>
                        </MembersSelect>
                      )}
                    </CollapsibleInner>
                  </CollapsibleWrapper>
                </ItemContent>
              </Item>
            );
          })}
        </Body>
        <Footer>
          <Button size="middle" type="default" onClick={modals.closeModal}>
            {t('cancel')}
          </Button>
          <Button size="middle" onClick={onSave} loading={isLoading}>
            {t('save')}
          </Button>
        </Footer>
      </Container>
    </StyledDrawer>
  );
};
